/**
 * Tipos, constantes y utilidades compartidas por los componentes del monitoreo
 * de consumo y costo (banner de estado, panel «Todos los servicios», formulario
 * de configuración). Todo el contenido de Firestore lo escribe el backend
 * (functions/src/costos); aquí solo se lee, salvo `costos_config/proveedores`.
 */

export const PANAMA_TZ = 'America/Panama';

// ── Fechas (Panamá es UTC−5 fijo, sin horario de verano) ────────────────────
export function panamaHoy(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: PANAMA_TZ }).format(new Date());
}

export function sumarDias(fecha: string, dias: number): string {
  const d = new Date(`${fecha}T00:00:00-05:00`);
  d.setDate(d.getDate() + dias);
  return new Intl.DateTimeFormat('en-CA', { timeZone: PANAMA_TZ }).format(d);
}

/** `YYYY-MM-DDTHH` (hora de Panamá) de hace `horas` horas. */
export function periodoHoraHace(horas: number): string {
  return new Date(Date.now() - horas * 3_600_000 - 5 * 3_600_000).toISOString().slice(0, 13);
}

/** Las `n` horas más recientes, de la más vieja a la actual, como `YYYY-MM-DDTHH`. */
export function ultimasHoras(n: number): string[] {
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) out.push(periodoHoraHace(i));
  return out;
}

export function etiquetaHora(periodo: string): string {
  const [f, h] = periodo.split('T');
  const [, m, d] = f.split('-');
  return `${d}/${m} ${h}h`;
}

export type FirestoreTs = { toDate?: () => Date } | null | undefined;

export function aFecha(ts: FirestoreTs): Date | null {
  return ts && typeof ts.toDate === 'function' ? ts.toDate() : null;
}

// ── Documentos de servicios ─────────────────────────────────────────────────
export type CategoriaServicio = 'video' | 'voz' | 'infra' | 'biometrico' | 'fijo';
export type FuenteCostoTipo = 'estimado' | 'facturado' | 'fijo';

export const CATEGORIAS: CategoriaServicio[] = ['video', 'voz', 'infra', 'biometrico', 'fijo'];

/** Colores de categoría: tonos medios que se leen igual en tema claro y oscuro. */
export const CATEGORIA_COLORES: Record<string, string> = {
  video: '#6366f1',
  voz: '#14b8a6',
  infra: '#f59e0b',
  biometrico: '#ec4899',
  fijo: '#8b5cf6',
};

export interface ServicioUsoDoc {
  categoria: string;
  proveedor: string;
  servicio: string;
  nombre: string;
  unidad: string;
  granularidad?: string;
  periodo: string;
  fecha: string;
  hora?: string;
  cantidad: number;
  costo: number | null;
  fuenteCosto?: FuenteCostoTipo | string;
  origen?: string;
  desglose?: Record<string, number>;
}

// ── Fuentes de monitoreo ────────────────────────────────────────────────────
export type EstadoFuente =
  | 'ok' | 'no_configurado' | 'sin_permiso' | 'error' | 'sin_datos' | 'desactualizado';

export interface FuenteDoc {
  id: string;
  nombre?: string;
  categoria?: string;
  descripcion?: string;
  obligatoria?: boolean;
  intervaloMin?: number;
  requisito?: string;
  estado?: EstadoFuente;
  mensaje?: string;
  detalle?: unknown;
  ultimaEjecucion?: FirestoreTs;
  ultimoExito?: FirestoreTs;
}

export const ESTADO_FUENTE_LABELS: Record<EstadoFuente, string> = {
  ok: 'Funcionando',
  no_configurado: 'Sin configurar',
  sin_permiso: 'Sin permiso',
  error: 'Con error',
  sin_datos: 'Sin datos',
  desactualizado: 'Desactualizado',
};

/** Estado efectivo: un «ok» cuyo último éxito es más viejo que 3 intervalos pasa a «desactualizado». */
export function estadoEfectivo(f: FuenteDoc, ahora = Date.now()): EstadoFuente {
  const estado = f.estado ?? 'sin_datos';
  if (estado !== 'ok') return estado;
  const exito = aFecha(f.ultimoExito);
  const intervalo = f.intervaloMin ?? 0;
  if (exito && intervalo > 0 && ahora - exito.getTime() > 3 * intervalo * 60_000) return 'desactualizado';
  return 'ok';
}

// ── Consumo del plan de LiveKit ─────────────────────────────────────────────
export interface ConsumoPlanDoc {
  plan?: string;
  mes?: string;
  minutos?: number;
  minutosIncluidos?: number;
  pctMinutos?: number;
  egressMin?: number;
  egressIncluidos?: number;
  pctEgress?: number;
  gb?: number;
  gbIncluidos?: number;
  pctGb?: number;
  transferencia?: 'real' | 'estimada';
  cuotaUsd?: number;
  excedenteUsd?: number;
  totalMesUsd?: number;
  proyeccionMesUsd?: number;
  diasTranscurridos?: number;
  diasDelMes?: number;
}

// ── Sesiones de LiveKit abiertas de más ─────────────────────────────────────
export interface SalaLarga {
  nombre: string;
  edadMin: number;
  humanos: number;
  agentes: number;
  participantes?: string[];
  consultaEstado: string | null;
  desconocida?: boolean;
  primeraVezMs?: number;
  ultimoAvisoMs?: number;
}

export interface SesionesLivekitDoc {
  actualizadoEn?: FirestoreTs;
  umbralMin?: number;
  proveedor?: string;
  totalSalas?: number;
  totalLargas?: number;
  salas?: SalaLarga[];
}

// ── Configuración (costos_config/proveedores) ───────────────────────────────
// Copia de PRECIOS_DEFECTO y PLAN_LIVEKIT_DEFECTO de functions/src/costos/tipos.ts.
// Si se cambian allá, hay que cambiarlos aquí.
export const PRECIOS_DEFECTO = {
  firestore_lecturas_100k: 0.06,
  firestore_escrituras_100k: 0.18,
  firestore_borrados_100k: 0.02,
  functions_millon_invocaciones: 0.40,
  logging_gib: 0.50,
  storage_gb_mes: 0.020,
  storage_salida_gb: 0.12,
  hosting_gb: 0.15,
  rtdb_gb: 1.0,
  livekit_min_participante: 0.0005,
  livekit_egress_pista_min: 0.001,
  livekit_gb: 0.12,
};
export type ClavePrecio = keyof typeof PRECIOS_DEFECTO;

export const PLAN_LIVEKIT_DEFECTO = {
  plan: 'ship' as 'ship' | 'scale' | 'build',
  cuotaMensualUsd: 50,
  minutosIncluidos: 150_000,
  gbIncluidos: 250,
  egressPistaMinIncluidos: 600,
  sesionLargaMin: 60,
};

export const PRECIOS_ETIQUETAS: Array<{ clave: ClavePrecio; etiqueta: string; unidad: string }> = [
  { clave: 'firestore_lecturas_100k', etiqueta: 'Firestore · lecturas', unidad: 'USD por 100 mil' },
  { clave: 'firestore_escrituras_100k', etiqueta: 'Firestore · escrituras', unidad: 'USD por 100 mil' },
  { clave: 'firestore_borrados_100k', etiqueta: 'Firestore · borrados', unidad: 'USD por 100 mil' },
  { clave: 'functions_millon_invocaciones', etiqueta: 'Funciones y Cloud Run · invocaciones', unidad: 'USD por millón' },
  { clave: 'logging_gib', etiqueta: 'Cloud Logging · ingesta', unidad: 'USD por GiB' },
  { clave: 'storage_gb_mes', etiqueta: 'Cloud Storage · almacenado', unidad: 'USD por GB al mes' },
  { clave: 'storage_salida_gb', etiqueta: 'Cloud Storage · salida a internet', unidad: 'USD por GB' },
  { clave: 'hosting_gb', etiqueta: 'Firebase Hosting · descarga', unidad: 'USD por GB' },
  { clave: 'rtdb_gb', etiqueta: 'Realtime Database · descarga', unidad: 'USD por GB' },
  { clave: 'livekit_min_participante', etiqueta: 'LiveKit · minuto de participante (excedente)', unidad: 'USD por minuto' },
  { clave: 'livekit_egress_pista_min', etiqueta: 'LiveKit · minuto de grabación por pista (excedente)', unidad: 'USD por minuto' },
  { clave: 'livekit_gb', etiqueta: 'LiveKit · transferencia de datos (excedente)', unidad: 'USD por GB' },
];

export interface CostoFijoForm {
  id: string;
  nombre: string;
  mensualUsd: string;
  categoria: CategoriaServicio;
}

// ── Validación de precios ───────────────────────────────────────────────────
export type EstadoPrecio = 'actualizado' | 'sin_cambio' | 'revisar' | 'sin_datos' | 'error' | 'manual';

export interface ResultadoPrecioDoc {
  clave: string;
  nombre: string;
  unidad: string;
  anterior: number | null;
  nuevo: number | null;
  estado: EstadoPrecio;
  fuente: string;
  nota?: string;
}

export interface ResumenPreciosDoc {
  actualizados: number;
  sinCambio: number;
  revisar: number;
  sinDatos: number;
  errores: number;
  manuales: number;
}

/** costos_config/precios_estado: lo escribe el backend al validar los precios. */
export interface PreciosEstadoDoc {
  ultimaActualizacion?: FirestoreTs;
  ultimoIntento?: FirestoreTs;
  ultimoIntentoFallo?: boolean;
  ultimoOrigen?: 'auto' | 'manual';
  resumen?: ResumenPreciosDoc;
  resultados?: ResultadoPrecioDoc[];
  enCurso?: { desde?: FirestoreTs } | null;
}

export interface CambioPrecioDoc {
  en: number;
  clave: string;
  nombre: string;
  unidad: string;
  anterior: number | null;
  nuevo: number | null;
  fuente: string;
  origen: string;
}

/** Los precios se validan al entrar a la página si llevan más de esta cantidad de días sin validarse. */
export const DIAS_VIGENCIA_PRECIOS = 7;

export const ESTADO_PRECIO_LABELS: Record<EstadoPrecio, string> = {
  actualizado: 'Actualizado',
  sin_cambio: 'Sin cambio',
  revisar: 'A revisar',
  sin_datos: 'Sin datos',
  error: 'Con error',
  manual: 'Manual',
};

/** ¿Hay que validar los precios ahora? (El servidor lo vuelve a decidir: esto solo evita una llamada inútil.) */
export function preciosVencidos(ultima: Date | null, ahora = Date.now()): boolean {
  return !ultima || ahora - ultima.getTime() > DIAS_VIGENCIA_PRECIOS * 86_400_000;
}

/** Un precio en dólares con los decimales que hagan falta (0.0005 y 9 se ven bien). */
export function fmtPrecio(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n >= 100) return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  return String(Number(n.toPrecision(4)));
}
